// Historia 120 — Bloque 19 (Jupiter, `features/S178-bloqueo-unico-y-contrato-jupiter.feature`, S179):
// LA HIGIENE DE CONTRATO.
//
// `BatchDuplicateJob` (E-B5) and `BatchInvalidTargetStatus` (E-B6) are the SERVER's safety net for
// N2/N3/N5 (`03-dominio.md` §8.3) -- "the interface should never send it". This module is what makes
// that true STRUCTURALLY, not by discipline: the three request builders below can only ever produce
// a job list with no repeated (fabricationOrderId, jobReferenceId) pair, never an empty one, and --
// for the start gesture, the only one that carries a target status -- a `statusId` that is always an
// active status resolved by code. If Sun ever answered one of these two literals despite these
// guarantees, it is a CLIENT defect (see `isBatchContractViolation` below), never a message to show
// the operator.
import { apiGet } from '../../../../generic/Api_Functions'
import { TARGET_STATUS_CODE } from '../../FabricationOrderJobReference/PlannedJobs/utils/jobStatus'

// The two active status codes a batch gesture may resolve/send, reusing the SAME source as the
// individual actions (`TARGET_STATUS_CODE`) -- one name per code in the whole app, never a second
// literal invented here.
export const BATCH_ACTIVE_STATUS_CODE = {
  PREPARATION: TARGET_STATUS_CODE.PREPARATION,
  EXECUTION: TARGET_STATUS_CODE.START
}

const ACTIVE_STATUS_CODES = Object.values(BATCH_ACTIVE_STATUS_CODE)

const jobPairKey = (job) => `${job?.fabricationOrderId}:${job?.jobReferenceId}`

/**
 * S179 — the pair (fabricationOrderId, jobReferenceId) never repeats within a gesture's job list.
 * Keeps the first occurrence; later repeats are dropped before the request is ever built, so
 * `BatchDuplicateJob` (E-B5) cannot be provoked from here.
 *
 * @param {Array<{fabricationOrderId: number, jobReferenceId: number}>} jobs
 * @returns {Array<{fabricationOrderId: number, jobReferenceId: number}>}
 */
export const dedupeJobPairs = (jobs) => {
  const list = Array.isArray(jobs) ? jobs : []
  const seen = new Set()
  return list.filter((job) => {
    const key = jobPairKey(job)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * S179 — a gesture's job list is never sent empty (`BatchSelectionEmpty`/E-B1 is a server safety
 * net too, but RN23 already keeps an empty selection from reaching this point on the interface
 * side). Building one from zero jobs is therefore a CLIENT defect, so this throws instead of
 * quietly producing `{jobs: []}`.
 */
const assertNonEmptyJobList = (jobs, gestureName) => {
  if (jobs.length === 0) {
    throw new Error(`BatchContractViolation: ${gestureName} built with an empty job list`)
  }
  return jobs
}

const buildJobRef = (job) => ({ fabricationOrderId: job.fabricationOrderId, jobReferenceId: job.jobReferenceId })

/**
 * S179/§8.2-bis — `statusId` travels PER JOB, resolved by CODE, ONCE per code per gesture (never
 * once per job): `GET fabricationOrderStatus/byCode/{code}`, the exact same query
 * `useJobStatusChange.js`/`useJobModeTransition.js` already use for the individual actions.
 *
 * @param {string[]} codes may repeat; only the distinct ones are actually requested.
 * @returns {Promise<Record<string, number>>} map of code -> statusId.
 */
export const resolveStatusIdsByCode = async (codes) => {
  const uniqueCodes = Array.from(new Set(codes))
  const entries = await Promise.all(
    uniqueCodes.map(async (code) => {
      const status = await apiGet('fabricationOrderStatus/byCode/' + code)
      return [code, status?.id]
    })
  )
  return Object.fromEntries(entries)
}

/**
 * N2 — builds `BatchStartRequestDto` (`03-dominio.md` §8.2, `contrato-sun.md` §2 lote 3):
 * `{deviceId, workerId, jobs:[{fabricationOrderId, jobReferenceId, statusId}]}`. No
 * `selectionType`: N2 only ever accepts the explicit confirmed list (POL4/RN18).
 *
 * RN26/§8.2-bis: a job whose `managePreparation` is not `true` ALWAYS travels with the EXECUTION
 * status id -- it is never omitted from the gesture and never marked invalid. A job that does
 * manage preparation travels with whichever active code the operator chose ONCE for the whole
 * gesture (`chosenModeCode`, D-I6) -- default EXECUTION, so a gesture with no preparation-managing
 * job at all needs no mode question (RN27).
 *
 * @param {{
 *   deviceId: number,
 *   workerId: number,
 *   jobs: Array<{fabricationOrderId: number, jobReferenceId: number, managePreparation?: boolean}>,
 *   chosenModeCode?: string
 * }} params `chosenModeCode` must be one of `BATCH_ACTIVE_STATUS_CODE` -- anything else is rejected
 *   before any network call, so an invalid target status can never reach `BatchInvalidTargetStatus`
 *   (E-B6).
 * @returns {Promise<{deviceId: number, workerId: number, jobs: Array<{fabricationOrderId: number, jobReferenceId: number, statusId: number}>}>}
 */
export const buildBatchStartRequest = async ({ deviceId, workerId, jobs, chosenModeCode = BATCH_ACTIVE_STATUS_CODE.EXECUTION }) => {
  if (!ACTIVE_STATUS_CODES.includes(chosenModeCode)) {
    throw new Error(`BatchContractViolation: '${chosenModeCode}' is not an active status code`)
  }

  const deduped = assertNonEmptyJobList(dedupeJobPairs(jobs), 'batchStart')
  const codeOf = (job) => (job?.managePreparation ? chosenModeCode : BATCH_ACTIVE_STATUS_CODE.EXECUTION)
  const statusIdsByCode = await resolveStatusIdsByCode(deduped.map(codeOf))

  return {
    deviceId,
    workerId,
    jobs: deduped.map((job) => ({ ...buildJobRef(job), statusId: statusIdsByCode[codeOf(job)] }))
  }
}

/**
 * N3 — builds `BatchPauseRequestDto`: `{deviceId, workerId, batchId, jobs:[{fabricationOrderId, jobReferenceId}]}`.
 * `jobs` is used server-side only for the contract checks (E-B1/E-B5) -- who actually gets paused is
 * always every effective member, derived from `batchId` (RN28/RN52).
 *
 * @param {{deviceId: number, workerId: number, batchId: string, jobs: Array<{fabricationOrderId: number, jobReferenceId: number}>}} params
 * @returns {{deviceId: number, workerId: number, batchId: string, jobs: Array<{fabricationOrderId: number, jobReferenceId: number}>}}
 */
export const buildBatchPauseRequest = ({ deviceId, workerId, batchId, jobs }) => ({
  deviceId,
  workerId,
  batchId,
  jobs: assertNonEmptyJobList(dedupeJobPairs(jobs), 'batchPause').map(buildJobRef)
})

/**
 * N5 — builds `BatchFinishRequestDto`: `{deviceId, workerId, batchId, jobs:[{fabricationOrderId, jobReferenceId}], confirmReportIncompleteAsNok}`.
 * `confirmReportIncompleteAsNok` is a primitive boolean, absent ⇒ `false` (same as today's
 * individual finish, RN69). `jobs` is used server-side only for the contract checks (E-B1/E-B5) --
 * who actually gets finished is always every effective member, derived from `batchId` (RN43/RN52).
 *
 * @param {{deviceId: number, workerId: number, batchId: string, jobs: Array<{fabricationOrderId: number, jobReferenceId: number}>, confirmReportIncompleteAsNok?: boolean}} params
 * @returns {{deviceId: number, workerId: number, batchId: string, jobs: Array<{fabricationOrderId: number, jobReferenceId: number}>, confirmReportIncompleteAsNok: boolean}}
 */
export const buildBatchFinishRequest = ({
  deviceId,
  workerId,
  batchId,
  jobs,
  confirmReportIncompleteAsNok = false,
  // S259/D-SP28 (ENMIENDA 10) -- SIEMPRE EXPLÍCITA, nunca ausente, también en la primera llamada:
  // el cuerpo es idéntico campo a campo entre las tres peticiones de la cascada, y un campo que
  // aparece y desaparece convierte esa comparación en una comparación con excepciones. Es primitivo,
  // no el `Boolean` tri-estado del cierre individual (`interruptInOtherDevices`), que lo es para no
  // romper a MORTE: N4/N5 no tienen consumidor externo.
  interruptInOtherDevicesConfirmed = false
}) => ({
  deviceId,
  workerId,
  batchId,
  jobs: assertNonEmptyJobList(dedupeJobPairs(jobs), 'batchFinish').map(buildJobRef),
  confirmReportIncompleteAsNok,
  interruptInOtherDevicesConfirmed
})

// S179 — the two literals `03-dominio.md` §8.3 (E-B5/E-B6) freezes as CONTRACT errors: "the
// interface should never send it". If either ever arrived from Sun despite the three builders
// above, it is a defect of THIS client, not a situation the operator caused -- a caller translating
// a `400 MessageDto` into an alert must exclude these two and report/log them instead.
export const BATCH_CONTRACT_VIOLATION_MESSAGES = ['BatchDuplicateJob', 'BatchInvalidTargetStatus']

export const isBatchContractViolation = (message) => BATCH_CONTRACT_VIOLATION_MESSAGES.includes(message)
